/**
 * Availability tab: in-stock % of top sellers, lost sales from exact out-of-stock days, SKUs whose stock is
 * not tracked, running out, excess and aged stock, with the actions they imply.
 * Props: data (GET /api/review/availability), showCosts (admin: stock valued at cost as well as list price).
 */
import React from 'react';
import { CoverBadge } from '../sales/views/common';
import { ActionList, DataTable, Empty, Metric, Money, Notes, Num, Pct, Section, SkuCell } from './ReviewPieces';

const stockValue = (row, showCosts) => (showCosts && row.stock_value_at_cost != null ? row.stock_value_at_cost : row.stock_value_mrp);

export const AvailabilityTab = ({ data, showCosts }) => {
  if (!data) return null;
  const s = data.in_stock || {};
  const valueLabel = showCosts ? 'Value at cost' : 'Value at list price';
  return (
    <div className="space-y-4" data-testid="review-availability-tab">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Metric label="In stock, top sellers" entry={{ value: s.in_stock_pct_weighted }} kind="percent"
          footnote={`Weighted by the 13 weeks before this one · ${s.in_stock_pct_unweighted ?? '—'}% by SKU count · basis ${s.basis || '—'}`} testId="review-in-stock" />
        <Metric label="Top sellers measured" entry={{ value: s.measured }} kind="number" footnote={`of ${s.top_sellers ?? '—'} top sellers · ${s.top_sellers_never_in_stock ?? 0} never in stock this week`} />
        <Metric label="Lost sales" entry={{ value: data.lost_sales?.total }} footnote="Exact out-of-stock days × velocity before the stock-out × ASP" testId="review-lost-sales" />
        <Metric label="Stock days known" entry={{ value: s.exact_days != null ? Math.round((s.exact_days / Math.max(1, (s.exact_days || 0) + (s.stale_days || 0) + (s.unknown_days || 0))) * 100) : null }} kind="percent"
          footnote={`${s.exact_days ?? 0} exact · ${s.stale_days ?? 0} stale · ${s.unknown_days ?? 0} unknown SKU-days`} />
      </div>

      <Section title="Lost sales by SKU" subtitle="Only days with an exact stock reading count; stale or unknown days are listed separately below." testId="review-lost-sales-table">
        <DataTable rows={data.lost_sales?.rows} empty="No lost sales from exact out-of-stock days this week." columns={[
          { key: 'sku', label: 'SKU', render: row => <SkuCell row={row} /> },
          { key: 'brand', label: 'Brand' },
          { key: 'oos_days', label: 'Days out', align: 'right', render: row => <Num v={row.oos_days} /> },
          { key: 'velocity', label: 'Velocity/day', align: 'right', render: row => <span className="tabular-nums">{row.velocity}</span> },
          { key: 'asp', label: 'ASP', align: 'right', render: row => <Money v={row.asp} /> },
          { key: 'lost_sales', label: 'Lost sales', align: 'right', render: row => <span className="font-semibold"><Money v={row.lost_sales} /></span> },
        ]} />
      </Section>

      {data.stock_not_tracked?.length > 0 && (
        <Section title="Stock not tracked" subtitle="Top sellers with stale or unknown stock on some days: upload bin stock (or connect the FBA inventory report) to measure them.">
          <DataTable rows={data.stock_not_tracked} columns={[
            { key: 'sku', label: 'SKU', render: row => <SkuCell row={row} /> },
            { key: 'basis', label: 'Basis' },
            { key: 'stale_days', label: 'Stale days', align: 'right', render: row => <Num v={row.stale_days} /> },
            { key: 'unknown_days', label: 'Unknown days', align: 'right', render: row => <Num v={row.unknown_days} /> },
            { key: 'last_stock_upload', label: 'Last stock upload', render: row => row.last_stock_upload || '—' },
          ]} />
        </Section>
      )}

      <Section title="Running out" subtitle="Cover below lead time + buffer days (at least 15), at the measured velocity." testId="review-running-out">
        <DataTable rows={data.running_out} empty="Nothing is running out at current velocity." columns={[
          { key: 'sku', label: 'SKU', render: row => <SkuCell row={row} /> },
          { key: 'stock', label: 'Stock', align: 'right', render: row => <Num v={row.stock} /> },
          { key: 'velocity', label: 'Velocity/day', align: 'right', render: row => <span className="tabular-nums" title={`Basis: ${row.velocity_basis}`}>{row.velocity}{row.velocity_basis !== 'measured' ? '*' : ''}</span> },
          { key: 'cover', label: 'Cover', align: 'right', render: row => <CoverBadge days={row.cover_days} stock={row.stock} /> },
          { key: 'threshold_days', label: 'Need', align: 'right', render: row => <span className="tabular-nums">{row.threshold_days} d</span> },
          { key: 'reorder_units', label: 'Reorder', align: 'right', render: row => <Num v={row.reorder_units} /> },
          { key: 'risk', label: 'Sales at risk', align: 'right', render: row => <span className="font-semibold"><Money v={row.lost_sales_risk} /></span> },
        ]} />
        <p className="mt-2 text-[11px] text-gray-500">* velocity estimated from the category median or calendar days (fewer than 7 measured in-stock days).</p>
      </Section>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section title="Excess stock" subtitle={`Over 120 days of cover · ${valueLabel.toLowerCase()}`} right={<span className="text-sm font-semibold tabular-nums"><Money v={showCosts ? data.excess_value_at_cost : data.excess_value_mrp} /></span>}>
          <DataTable rows={data.excess} empty="No excess stock." columns={[
            { key: 'sku', label: 'SKU', render: row => <SkuCell row={row} /> },
            { key: 'stock', label: 'Stock', align: 'right', render: row => <Num v={row.stock} /> },
            { key: 'cover', label: 'Cover', align: 'right', render: row => <span className="tabular-nums">{row.cover_days == null ? '—' : `${Math.round(row.cover_days)} d`}</span> },
            { key: 'excess_units', label: 'Excess units', align: 'right', render: row => <Num v={row.excess_units} /> },
            { key: 'value', label: valueLabel, align: 'right', render: row => <Money v={stockValue(row, showCosts)} /> },
          ]} />
        </Section>
        <Section title="Aged stock" subtitle={`No sale in 90 days · ${valueLabel.toLowerCase()}`} right={<span className="text-sm font-semibold tabular-nums"><Money v={showCosts ? data.aged_value_at_cost : data.aged_value_mrp} /></span>}>
          <DataTable rows={data.aged} empty="No aged stock." columns={[
            { key: 'sku', label: 'SKU', render: row => <SkuCell row={row} /> },
            { key: 'brand', label: 'Brand' },
            { key: 'stock', label: 'Stock', align: 'right', render: row => <Num v={row.stock} /> },
            { key: 'value', label: valueLabel, align: 'right', render: row => <Money v={stockValue(row, showCosts)} /> },
          ]} />
        </Section>
      </div>

      <Section title="Suggested actions">
        {data.actions?.length ? <ActionList actions={data.actions} testId="review-availability-actions" /> : <Empty>No availability actions this week.</Empty>}
      </Section>
      <Notes notes={data.notes} />
      <span className="sr-only"><Pct v={s.in_stock_pct_weighted} /></span>
    </div>
  );
};

export default AvailabilityTab;
